import CrossIcon from "@assets/icons/cross.svg";
import SetIcon from "@assets/icons/set.svg";
import TimeIcon from "@assets/icons/time.svg";
import { useI18n } from "@core/i18n/i18n";
import { analyticsService } from "@core/service-container";
import { Exercise, isDurationExercise } from "@domain/exercise/exercise";
import { e2eTestId } from "@e2e/test-ids";
import styled from "@emotion/styled";
import { TrackingContext } from "@ui/components/common/tracking-context-provider";
import { InputWithArrows } from "@ui/pages/program-builder/library/input-with-arrows";
import { colors } from "@ui/style/colors";
import { row } from "@ui/style/layout";
import React, { useContext, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";

interface ExerciseDetailsProps {
	exercise: Exercise;
	updateExercise: (updatedExercise: Exercise) => void;
	withArrows: boolean;
}

export const ExerciseDetails: React.FC<ExerciseDetailsProps> = ({ exercise, updateExercise, withArrows }) => {
	const trackingContext = useContext(TrackingContext);
	const repetitionNumberRef = useRef<ExerciseDetailsContentRef>(null);
	const setNumberRef = useRef<ExerciseDetailsContentRef>(null);
	const isExerciseDuration = isDurationExercise(exercise);

	return (
		<ExerciseDetailsContainer>
			<ExerciseDetailsContent
				ref={repetitionNumberRef}
				initial={exercise.units}
				type={isExerciseDuration ? "time" : "number"}
				onSuccess={units => {
					updateExercise({ ...exercise, units });
					analyticsService.trackEvent({
						category: trackingContext.category,
						action: isExerciseDuration ? "editDuration" : "editRepetitions",
						params: [units, exercise.title, exercise.id],
					});
				}}
				icon={isExerciseDuration ? <TimeIcon width="15" height="15" /> : <CrossIcon width="15" height="15" />}
				onTabKeyDown={() => setNumberRef.current?.focus()}
				withArrows={withArrows}
			/>
			<ExerciseDetailsContent
				ref={setNumberRef}
				initial={exercise.setCount}
				onSuccess={setCount => {
					updateExercise({ ...exercise, setCount });
					analyticsService.trackEvent({
						category: trackingContext.category,
						action: "editSets",
						params: [setCount, exercise.title, exercise.id],
					});
				}}
				icon={<SetIcon width="15" height="15" />}
				onTabKeyDown={() => repetitionNumberRef.current?.focus()}
				withArrows={withArrows}
			/>
		</ExerciseDetailsContainer>
	);
};

interface ExerciseDetailsContentRef {
	focus: () => void;
	blur: () => void;
}

const ExerciseDetailsContent = React.forwardRef<
	ExerciseDetailsContentRef,
	{
		initial: number;
		icon: React.ReactNode;
		onSuccess?: (value: number) => void;
		onTabKeyDown?: () => void;
		type?: "number" | "time";
		withArrows: boolean;
	}
>(({ initial, icon, type = "number", onSuccess, onTabKeyDown, withArrows }, ref) => {
	const [value, setValue] = useState(initial);
	const [error, setError] = useState(false);
	const [timeInputIsFocus, setTimeInputIsFocus] = useState(false);
	const { formatDuration } = useI18n();

	useEffect(() => {
		setValue(initial);
	}, [initial]);

	const innerRef = useRef<ExerciseDetailsContentRef>(null);

	useImperativeHandle<ExerciseDetailsContentRef | null, ExerciseDetailsContentRef | null>(ref, () => innerRef.current);

	const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
		switch (event.key) {
			case "Tab":
				event.preventDefault();
				innerRef.current?.blur();
				setTimeInputIsFocus(false);
				onSuccess?.(value);
				onTabKeyDown?.();
				break;
			case "Enter":
				innerRef.current?.blur();
				setTimeInputIsFocus(false);
				onSuccess?.(value);
				break;
			case "Escape":
				setTimeInputIsFocus(false);
				innerRef.current?.blur();
				setValue(initial);
				break;
			default:
				break;
		}
	};

	const inputProps = useMemo(() => {
		const unFocus = () => {
			innerRef.current?.blur();
			onSuccess?.(value);
		};
		return type === "number"
			? { onBlur: unFocus }
			: {
					onBlur: () => {
						setTimeInputIsFocus(false);
						unFocus();
					},
					onFocus: () => setTimeInputIsFocus(true),
				};
	}, [onSuccess, type, value]);

	return (
		<DetailField
			ref={innerRef}
			errorMode={error}
			icon={timeInputIsFocus ? null : icon}
			label={type === "time" ? formatDuration(value).slice(1) : value.toString()}
			input={
				<InputWithArrows
					type={type}
					value={value}
					onValueChange={setValue}
					onKeyDown={handleKeyDown}
					onError={setError}
					withArrows={withArrows}
					{...inputProps}
				/>
			}
		/>
	);
});
ExerciseDetailsContent.displayName = "ExerciseDetailsContent";

const DetailField = React.forwardRef<
	ExerciseDetailsContentRef,
	{ icon: React.ReactNode; input: React.ReactNode; label: string; errorMode?: boolean }
>(({ icon, input, label, errorMode }, ref) => {
	const [editing, setEditing] = useState(false);

	let detailMode: "error" | "focus" | undefined;
	if (errorMode) {
		detailMode = "error";
	} else if (editing) {
		detailMode = "focus";
	}

	useImperativeHandle(ref, () => ({ focus: () => setEditing(true), blur: () => setEditing(false) }), []);

	return (
		<Details
			data-test={e2eTestId.createTemplatePage.detailField}
			mode={detailMode}
			textCursor={!editing}
			className="p-size-s-bold"
			onClick={e => {
				e.stopPropagation();
				!editing && setEditing(true);
			}}
		>
			{icon}
			{editing ? input : <span style={{ lineHeight: "1.375rem" }}>{label}</span>}
		</Details>
	);
});
DetailField.displayName = "DetailField";

const ExerciseDetailsContainer = styled.div`
	${row(8)};
`;

const getDetailBorderColor = (mode?: "error" | "focus"): string => {
	if (mode === "error") {
		return colors.rejection;
	}

	if (mode === "focus") {
		return colors.primary;
	}

	return colors.primary25;
};

const Details = styled.div<{ textCursor: boolean; mode?: "error" | "focus" }>`
	flex: 1;
	position: relative;
	overflow: hidden;
	min-height: 40px;
	border-radius: 16px;
	${row(4, "center", "center")};
	color: ${colors.grey};
	cursor: ${props => (props.textCursor ? "text" : undefined)};

	border-radius: 8px;
	border: 1px solid;
	border-color: ${({ mode }) => getDetailBorderColor(mode)};

	:hover {
		border-color: ${colors.primary};
	}

	/* Chrome, Safari, Edge, Opera */

	input::-webkit-outer-spin-button,
	input::-webkit-inner-spin-button {
		-webkit-appearance: none;
		margin: 0;
	}

	/* Firefox */

	input[type="number"] {
		appearance: textfield;
		-moz-appearance: textfield;
	}
`;
